/**
 * Centralized registry of all application routes.
 * This ensures type safety and allows for easy refactoring of internal paths.
 */
export const APP_PATHS = {
  PUBLIC: {
    LANDING: '/',
    CONTACT: '/contact',
    LOGIN: '/login',
    LOGIN_ROLE: '/login/:role',
    PASSWORD_CHANGE: '/password-change',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password/:token',
    TWO_FACTOR_AUTH: '/2fa-auth',
    SECURE_NOTE: '/secure-note',
  },
  ADMIN: {
    DASHBOARD: '/admin/dashboard',
    USER_MANAGEMENT: {
      ROOT: '/admin/user-management',
      CREATE: '/admin/user-management/create',
      BULK_CREATE: '/admin/user-management/bulk-create',
      USERS_BY_ROLE: '/admin/users/:roleKey',
    },
    SCHOLARSHIP_MANAGEMENT: {
      ROOT: '/admin/scholarship-management',
      APPLICATIONS: '/admin/scholarship-management/applications',
      APPLICATION_DETAILS: '/admin/scholarship-management/applications/:applicationId',
      NEW_APPLICATION: '/admin/scholarship-management/applications/new',
    },
    FEEDBACK_MANAGEMENT: '/admin/feedback-management',
    FEEDBACK_DETAILS: '/admin/feedback-management/:feedbackId',
    SYSTEM_CONFIGURATION: {
      ROOT: '/admin/system-configuration',
      ROLES: '/admin/system-configuration/roles',
      ROLE_DETAILS: '/admin/system-configuration/roles/:roleId',
      NAVIGATION: '/admin/system-configuration/navigation',
      DYNAMIC_UI: '/admin/system-configuration/dynamic-ui',
      WORKFLOWS: '/admin/system-configuration/workflows',
    },
    REPORTS_LOGS: '/admin/reports-logs',
    ANALYTICS: '/admin/analytics',
    OBSERVABILITY: {
      ROOT: '/admin/observability',
      DLQ: '/admin/observability/dlq',
    },
    WORKFLOWS: {
      ROOT: '/admin/workflows',
      FORENSIC_AUDIT: '/admin/workflows/forensic-audit',
      OBSERVABILITY: '/admin/observability/workflow',
    },
    AUDIT_LOGS: '/admin/audit-logs',
    JIT_ACCESS: '/admin/jit-access',
    COMMUNICATION_RULES: {
      ROOT: '/admin/communication-rules',
      NEW: '/admin/communication-rules/new',
      EDIT: '/admin/communication-rules/:ruleId',
    },
    SECURITY_CENTER: '/admin/security-center',
    FINANCIAL_INSIGHTS: '/admin/financial-insights',
    SYSTEM_GOVERNANCE: '/admin/system-governance',
    PROFILE_SETTINGS: '/admin/profile-settings',
    PAYMENT_OPTIONS: '/admin/payment-options',
    FEE_STRUCTURES: '/admin/fee-structures',
    INSTALLMENT_PLANS: '/admin/installment-plans',
    NOTIFICATION_MANAGEMENT: '/admin/notification-management',
    NOTIFICATIONS: '/admin/notifications',
    ELIGIBILITY_CONFIG: '/admin/academic/eligibility-config',
    ACADEMIC_SESSIONS: '/admin/academic-sessions',
    PROMOTION_PREVIEW: '/admin/academic-sessions/:sessionId/preview',
    PROMOTION_RESULTS: '/admin/academic-sessions/:sessionId/results',
    ACADEMIC_RECORD_ENTRY: '/admin/academic-record-entry',
  },
  STUDENT: {
    DASHBOARD: '/student/dashboard',
    MAKE_PAYMENT: '/student/make-payment',
    PAYMENT_STATUS: '/student/payment-status',
    PAYMENT_HISTORY: '/student/payment-history',
    INSTALLMENT_SCHEDULE: '/student/installment-schedule',
    DOWNLOAD_RECEIPTS: '/student/download-receipts',
    NOTIFICATIONS: '/student/notifications',
    SUBMIT_FEEDBACK: '/student/submit-feedback',
    PROFILE: '/student/profile',
    MY_SCHOLARSHIPS: '/student/my-scholarships',
  },
  STAFF: {
    DASHBOARD: '/staff/dashboard',
    PAYMENT_VERIFICATION: '/staff/payment-verification',
    SCHOLARSHIP_APPROVALS: {
      ROOT: '/staff/scholarship-approvals',
      DETAILS: '/staff/scholarship-approvals/:applicationId',
    },
    STUDENT_ACCOUNT_INQUIRY: '/staff/student-account-inquiry',
    STUDENT_FINANCIAL_RECORD: '/staff/student-financial-record/:studentId',
    FEE_STRUCTURE_MANAGEMENT: '/staff/fee-structure-management',
    FEE_MANAGEMENT: '/staff/fee-management',
    INSTALLMENT_PLAN_CONFIGURATION: '/staff/installment-plan-configuration',
    FINANCIAL_REPORTS: '/staff/financial-reports',
    ANNOUNCEMENTS: '/staff/announcements',
    PAYMENT_TRENDS: '/staff/payment-trends',
    NOTIFICATIONS: '/staff/notifications',
    PROFILE: '/staff/profile',
    FEEDBACK: {
      ROOT: '/staff/feedback',
      DETAILS: '/staff/feedback/:feedbackId',
    },
    PROMOTION_PREVIEW: '/staff/academic-sessions/:sessionId/preview',
    PROMOTION_RESULTS: '/staff/academic-sessions/:sessionId/results',
    ACADEMIC_SESSIONS: '/staff/academic-sessions',
  },
  SEARCH: '/search',
} as const;
